'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { authBunja } from '@flipster/features/src/shared/auth/state';
import { earnSummaryBunja, getEarnSummaryOfCurrency } from '@flipster/features/src/shared/earn/state';
import { useRouter } from '@flipster/i18n/routing';
import { useOverlay } from '@flipster/overlay';
import { Button } from '@flipster/react/design-system/button';
import { hstack } from '@flipster/styled-system/patterns';
import { PusdtCurrency } from '@flipster/utils/constants/currency';
import segment from '@flipster/utils/segment';
import { EarnEvent } from '@flipster/utils/segment/events/earn';

import { mintBunja } from '@web/app/[locale]/(web)/_modal/MintFlowModal/state';
import { useWalletModal } from '@web/app/[locale]/(web)/_modal/WalletModal/hooks/useWalletModal';
import ManagePusdtModal from '@web/app/[locale]/(web)/_modal/premium-earn/manage-pusdt-modal';

import EarnActionModal from '../../earn-action-modal';

export const ActionButtons = ({ currency }: { currency: string }) => {
  const overlay = useOverlay();
  const router = useRouter();
  const { openWalletModal } = useWalletModal();
  const { isLoggedInAtom } = useBunja(authBunja);
  const { openMintModal } = useBunja(mintBunja);
  const { earnSummaryAtom } = useBunja(earnSummaryBunja);
  const earnSummary = useAtomValue(earnSummaryAtom);
  const { isMintAvailable, isTradeAvailable } = getEarnSummaryOfCurrency(currency, earnSummary);

  // Open action modal when mint and trade is available
  const needToOpenActions = isMintAvailable || isTradeAvailable;
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const isPusdt = currency === PusdtCurrency;

  const onClickAdd = () => {
    segment.track(EarnEvent.EarnManageClick, { button_text: 'Add', currency });
    overlay.open(<EarnActionModal currency={currency} />);
  };

  const onClickRedeem = () => {
    segment.track(EarnEvent.EarnManageClick, { button_text: 'Redeem', currency });
    openMintModal({ providerCurrency: currency, flowType: 'REDEEM' });
  };

  if (needToOpenActions) {
    return (
      <div className={hstack({ justify: 'center', gap: 8 })}>
        {isMintAvailable && (
          <Button
            label='Redeem'
            hierarchy='secondary'
            color='neutral'
            onClick={onClickRedeem}
            css={{ w: { base: '50%', tablet: 160 } }}
          />
        )}
        <Button
          label='Add'
          onClick={onClickAdd}
          css={{ w: isMintAvailable ? { base: '50%', tablet: 160 } : { base: '100%', tablet: 296 } }}
        />
      </div>
    );
  }

  return (
    <div className={hstack({ justify: 'center' })}>
      <Button
        hierarchy='primary'
        color='accent'
        size='medium'
        label={isPusdt ? 'Manage' : 'Deposit'}
        css={{ w: { base: '100%', tablet: 296 } }}
        onClick={() => {
          if (isLoggedIn) {
            segment.track(EarnEvent.EarnManageClick, { button_text: isPusdt ? 'Manage' : 'Deposit', currency });
            if (isPusdt) {
              overlay.open(<ManagePusdtModal />);
            } else {
              openWalletModal('deposit', { defaultCurrency: currency });
            }
          } else {
            router.push('/login');
          }
        }}
      />
    </div>
  );
};
